import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  CheckCircle2, X, Banknote, ExternalLink, RefreshCw, AlertTriangle, Filter,
  Clock, Hourglass, Send, Bell, FileText, Unlock, Check, Pencil, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";

// Iter-164 — 5-stage lifecycle surfaced on the Factures freelance page. Statuses with
// an `_virtual` flag exist only in the API response (not in db.freelance_invoices) —
// they are derived from the underlying client invoice + freelance_unlock state.
const STATUS_META = {
  AWAITING_UNLOCK:      { label: "En attente de déblocage",   color: "bg-blue-100 text-blue-800",       icon: Hourglass },
  AWAITING_SUBMISSION:  { label: "En attente de réception",   color: "bg-amber-100 text-amber-900",     icon: Send },
  INVOICE_UNDER_REVIEW: { label: "En attente de validation",  color: "bg-violet-100 text-violet-800",   icon: FileText },
  INVOICE_PROCESSING:   { label: "En attente de paiement",    color: "bg-orange-100 text-orange-900",   icon: Banknote },
  INVOICE_PAID:         { label: "Réglée",                    color: "bg-emerald-100 text-emerald-900", icon: CheckCircle2 },
  INVOICE_REJECTED:     { label: "Rejetée",                   color: "bg-red-100 text-red-800",         icon: AlertTriangle },
};

const STATUS_FILTERS = [
  { key: "ALL",                  label: "Toutes" },
  { key: "AWAITING_UNLOCK",      label: "En attente de déblocage" },
  { key: "AWAITING_SUBMISSION",  label: "En attente de réception" },
  { key: "INVOICE_UNDER_REVIEW", label: "En validation" },
  { key: "INVOICE_PROCESSING",   label: "En attente de paiement" },
  { key: "INVOICE_PAID",         label: "Réglées" },
  { key: "INVOICE_REJECTED",     label: "Rejetées" },
];

export default function FreelanceAdmin() {
  const { user } = useAuth();
  const isCEO = user?.role === "CEO";
  // Iter-193c — CEO + GESTIONNAIRE can override the payment_delay_days on a
  // validated invoice (INVOICE_PROCESSING or PAID). The PATCH endpoint traces
  // the change in freelance_invoice_audit_log.
  const canEditDelay = user && (user.role === "CEO" || user.role === "GESTIONNAIRE");
  const [editDelayInv, setEditDelayInv] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [rejectDialog, setRejectDialog] = useState(null);
  // Iter-193 — validation dialog asks for payment delay days.
  const [validateDialog, setValidateDialog] = useState(null);
  const [validateDelay, setValidateDelay] = useState(30);
  const [defaultDelay, setDefaultDelay] = useState(30);
  useEffect(() => {
    api.get("/settings").then((r) => {
      const d = r?.data?.payment_default_delay_days;
      if (d) { setDefaultDelay(d); setValidateDelay(d); }
    }).catch(() => {});
  }, []);
  const [rejectReason, setRejectReason] = useState("");
  const [busyId, setBusyId] = useState(null);
  // Iter-220 — Cabinet (portage) filter + sortable columns.
  const [cabinetFilter, setCabinetFilter] = useState("ALL");
  const [sortField, setSortField] = useState("submitted_at"); // user|cabinet|mission|submitted_at|status|amount
  const [sortDir, setSortDir] = useState("desc"); // asc|desc

  // Iter-228x — Payment dialog state + BAIM bank account list (used by the
  // "Compte débité" selector in the popin). Loaded lazily on mount because
  // the list is small (< 10 rows).
  const [payDialog, setPayDialog] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    api.get("/admin/bank-accounts").then((r) => {
      setBankAccounts(Array.isArray(r.data?.items) ? r.data.items : []);
    }).catch(() => {});
  }, []);

  // Iter-228x — Manual resend of the portage-company payment email on PAID
  // invoices (used when the original send failed silently or the cabinet
  // asks for a fresh copy).
  const resendPortageEmail = async (id) => {
    if (!window.confirm("Renvoyer l'email de règlement au cabinet de portage (avec freelancer en CC + PDF de la facture en pièce jointe) ?")) return;
    setBusyId(id);
    try {
      await api.post(`/freelance/admin/invoices/${id}/resend-portage-email`);
      toast.success("Email cabinet de portage renvoyé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du renvoi");
    } finally {
      setBusyId(null);
    }
  };

  const submitMarkPaid = async () => {    if (!payDialog) return;
    const inv = payDialog.invoice;
    if (!payDialog.paid_date) return toast.error("Date de paiement requise");
    if (!payDialog.payment_reference.trim()) return toast.error("Référence du règlement requise");
    setBusyId(inv.freelance_invoice_id);
    try {
      await api.post(`/freelance/admin/invoices/${inv.freelance_invoice_id}/mark-paid`, {
        paid_date: payDialog.paid_date,
        payment_reference: payDialog.payment_reference.trim(),
        payment_method: payDialog.payment_method,
        amount_paid: payDialog.amount_paid ? Number(payDialog.amount_paid) : null,
        bank_account_id: payDialog.bank_account_id || null,
        notes: payDialog.notes || "",
      });
      toast.success("Facture réglée — email envoyé au cabinet de portage");
      setPayDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du règlement");
    } finally {
      setBusyId(null);
    }
  };

  const load = async () => {
    try {
      const params = statusFilter === "ALL" ? "" : `?status=${statusFilter}`;
      const invR = await api.get(`/freelance/admin/invoices${params}`);
      setInvoices(Array.isArray(invR.data) ? invR.data : []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load error"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const toggleSel = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allProcessing = useMemo(() => invoices.filter((i) => i.status === "INVOICE_PROCESSING"), [invoices]);
  const selectedProcessing = useMemo(() => allProcessing.filter((i) => selected.has(i.freelance_invoice_id)), [allProcessing, selected]);

  // Iter-220 — Distinct cabinets (portage companies) seen in the current list,
  // used to populate the cabinet filter dropdown.
  const cabinets = useMemo(() => {
    const set = new Map();
    invoices.forEach((i) => {
      const name = i.portage_company_name || "—";
      set.set(name, (set.get(name) || 0) + 1);
    });
    return Array.from(set.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [invoices]);

  // Apply cabinet filter + sort to the visible rows.
  const visibleInvoices = useMemo(() => {
    let arr = invoices;
    if (cabinetFilter !== "ALL") {
      arr = arr.filter((i) => (i.portage_company_name || "—") === cabinetFilter);
    }
    const getKey = (r) => {
      switch (sortField) {
        case "user":         return (r.user_name || "").toLowerCase();
        case "cabinet":      return (r.portage_company_name || "").toLowerCase();
        case "mission":      return (r.mission_title || (r.missions_breakdown?.[0]?.mission_title) || "").toLowerCase();
        case "submitted_at": return r.submitted_at || r.client_invoice_sent_at || "";
        case "status":       return r.status || "";
        case "amount":       return Number(r.amount_ttc || r.amount_ht || 0);
        default:             return "";
      }
    };
    const sorted = [...arr].sort((a, b) => {
      const ka = getKey(a); const kb = getKey(b);
      if (ka < kb) return sortDir === "asc" ? -1 : 1;
      if (ka > kb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [invoices, cabinetFilter, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };
  const sortIcon = (field) => sortField === field ? (sortDir === "asc" ? "▲" : "▼") : "";

  const validateOne = async (id, payment_delay_days) => {
    setBusyId(id);
    try {
      await api.post(`/freelance/admin/invoices/${id}/validate`, { payment_delay_days });
      toast.success(`Facture validée — paiement attendu sous ${payment_delay_days} jours`);
      setValidateDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusyId(null); }
  };
  const markPaidOne = async (id) => {
    // Iter-228x — replaced silent POST with the dedicated dialog. Kept the
    // function as a thin opener so existing callers (single button) still work.
    const inv = invoices.find((i) => i.freelance_invoice_id === id);
    if (!inv) return toast.error("Facture introuvable");
    setPayDialog({
      invoice: inv,
      paid_date: new Date().toISOString().slice(0, 10),
      payment_reference: "",
      payment_method: "VIREMENT",
      amount_paid: String(inv.amount_ttc ?? ""),
      // Iter-234 — preselect the default MAD account (any currency mission).
      bank_account_id: (bankAccounts.find((b) => b.scope === "MAD" && b.is_default) || bankAccounts.find((b) => b.scope === "MAD") || {}).bank_account_id || "",
      notes: "",
    });
  };
  const bulkMarkPaid = async () => {
    if (selectedProcessing.length === 0) return;
    if (!window.confirm(`Mark ${selectedProcessing.length} invoice(s) as paid?`)) return;
    try {
      const r = await api.post("/freelance/admin/invoices/bulk-mark-paid", {
        invoice_ids: selectedProcessing.map((i) => i.freelance_invoice_id),
      });
      toast.success(`${r.data.updated} invoice(s) marked as paid`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const submitReject = async () => {
    if (!rejectReason.trim()) return toast.error("Motif requis");
    try {
      await api.post(`/freelance/admin/invoices/${rejectDialog.freelance_invoice_id}/reject`, { reason: rejectReason.trim() });
      toast.success("Invoice rejected");
      setRejectDialog(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // Iter-164 — virtual-row actions (CEO/admin nudges before the freelance has submitted).
  const remindFreelance = async (clientInvoiceId, userId, virtualId) => {
    setBusyId(virtualId);
    try {
      await api.post(`/freelance/admin/awaiting/${clientInvoiceId}/${userId}/remind`);
      toast.success("Relance envoyée au freelance");
    } catch (e) { toast.error(e?.response?.data?.detail || "Reminder failed"); }
    finally { setBusyId(null); }
  };
  const forceUnlockClientInvoice = async (clientInvoiceId, virtualId) => {
    if (!window.confirm("Débloquer immédiatement la facture freelance pour ce client (override CEO) ?")) return;
    setBusyId(virtualId);
    try {
      await api.post(`/invoices/${clientInvoiceId}/freelance-unlock/force-now`);
      toast.success("Facture freelance débloquée");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Force unlock failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]">
      <div className="text-[11px] uppercase tracking-[0.25em] text-brand-navy-500 mb-3 font-mono">Freelance</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-3">Factures freelance</h1>
      <p className="text-sm text-zinc-600 max-w-3xl mb-6">
        Cycle de vie complet de chaque facture freelance — depuis l'envoi de la facture client (en attente de
        déblocage) jusqu'au règlement final. Les lignes <em>en attente de déblocage</em> et <em>en attente de
        réception</em> sont virtuelles : elles disparaissent dès que le freelance soumet sa propre facture.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-5 px-4 py-3 bg-white border border-brand-navy/15 rounded-lg" data-testid="freelance-filters">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand-navy-500 font-mono"><Filter size={13} /> Statut</div>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => { setStatusFilter(f.key); setSelected(new Set()); }}
            data-testid={`filter-status-${f.key}`}
            className={`px-3 h-8 rounded-full text-xs font-medium transition-colors ${
              statusFilter === f.key ? "bg-brand-navy text-white" : "bg-brand-cream-100 text-brand-ink hover:bg-brand-orange-100 hover:text-brand-orange-600"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button onClick={load} title="Actualiser" data-testid="freelance-refresh" className="ml-auto p-1.5 rounded-md hover:bg-brand-cream-200">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Iter-220 — Cabinet (portage) filter row. */}
      {cabinets.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-5 px-4 py-3 bg-white border border-brand-navy/15 rounded-lg" data-testid="freelance-cabinet-filters">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand-navy-500 font-mono">
            <Filter size={13} /> Cabinet
          </div>
          <button
            onClick={() => setCabinetFilter("ALL")}
            data-testid="filter-cabinet-ALL"
            className={`px-3 h-8 rounded-full text-xs font-medium transition-colors ${
              cabinetFilter === "ALL" ? "bg-brand-navy text-white" : "bg-brand-cream-100 text-brand-ink hover:bg-brand-orange-100 hover:text-brand-orange-600"
            }`}
          >
            Tous ({invoices.length})
          </button>
          {cabinets.map(([name, n]) => (
            <button
              key={name}
              onClick={() => setCabinetFilter(name)}
              data-testid={`filter-cabinet-${name}`}
              className={`px-3 h-8 rounded-full text-xs font-medium transition-colors ${
                cabinetFilter === name ? "bg-brand-navy text-white" : "bg-brand-cream-100 text-brand-ink hover:bg-brand-orange-100 hover:text-brand-orange-600"
              }`}
            >
              {name} ({n})
            </button>
          ))}
        </div>
      )}

      {selectedProcessing.length > 0 && (
        <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-300 rounded-lg flex items-center justify-between" data-testid="bulk-paid-bar">
          <div className="text-sm text-emerald-900">
            <strong>{selectedProcessing.length}</strong> facture(s) en attente de paiement sélectionnée(s)
          </div>
          <button
            onClick={bulkMarkPaid}
            data-testid="bulk-mark-paid-btn"
            className="inline-flex items-center gap-2 px-4 h-9 bg-emerald-600 text-white text-sm font-semibold rounded-md hover:bg-emerald-700"
          >
            <Banknote size={14} /> Mark as paid
          </button>
        </div>
      )}

      <div className="bg-white border border-brand-navy/15 rounded-lg md:overflow-x-auto md:overflow-hidden">
        <div className="hidden md:grid md:[grid-template-columns:2.5rem_2fr_2fr_1.6fr_1fr_2fr_1fr_1.4fr] md:min-w-[1100px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-brand-navy/10 font-mono">
          <div>—</div>
          <button type="button" onClick={() => toggleSort("user")} data-testid="sort-user" className="text-left hover:text-brand-navy">Freelance / mois {sortIcon("user")}</button>
          <button type="button" onClick={() => toggleSort("mission")} data-testid="sort-mission" className="text-left hover:text-brand-navy">Mission / client {sortIcon("mission")}</button>
          <button type="button" onClick={() => toggleSort("cabinet")} data-testid="sort-cabinet" className="text-left hover:text-brand-navy">Cabinet {sortIcon("cabinet")}</button>
          <button type="button" onClick={() => toggleSort("submitted_at")} data-testid="sort-submitted_at" className="text-left hover:text-brand-navy">Soumis le {sortIcon("submitted_at")}</button>
          <button type="button" onClick={() => toggleSort("status")} data-testid="sort-status" className="text-left hover:text-brand-navy">Statut {sortIcon("status")}</button>
          <button type="button" onClick={() => toggleSort("amount")} data-testid="sort-amount" className="text-left hover:text-brand-navy">Montant {sortIcon("amount")}</button>
          <div className="text-right">Actions</div>
        </div>
        {visibleInvoices.length === 0 && <div className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="freelance-invoices-empty">No invoice {statusFilter !== "ALL" && "dans ce statut"}.</div>}
        {visibleInvoices.map((inv) => {
          const meta = STATUS_META[inv.status] || { label: inv.status, color: "bg-zinc-100 text-zinc-700", icon: Clock };
          const Icon = meta.icon;
          const canSelect = inv.status === "INVOICE_PROCESSING";
          const isVirtual = !!inv.is_virtual;
          const days = inv.freelance_unlock_days_remaining;
          const overdue = !!inv.freelance_unlock_overdue;
          return (
            <div key={inv.freelance_invoice_id} data-testid={`fl-inv-${inv.freelance_invoice_id}`} className={`flex flex-col gap-2 md:grid md:[grid-template-columns:2.5rem_2fr_2fr_1.6fr_1fr_2fr_1fr_1.4fr] md:items-center md:min-w-[1100px] md:gap-x-3 md:gap-y-0 px-5 py-4 border-b border-brand-navy/10 last:border-b-0 hover:bg-brand-cream-50 ${isVirtual ? "bg-blue-50/30" : ""}`}>
              <div>
                <input
                  type="checkbox"
                  disabled={!canSelect}
                  checked={selected.has(inv.freelance_invoice_id)}
                  onChange={() => toggleSel(inv.freelance_invoice_id)}
                  data-testid={`fl-inv-check-${inv.freelance_invoice_id}`}
                />
              </div>
              <div>
                <div className="font-medium text-sm">{inv.user_name || "—"}</div>
                <div className="text-xs text-zinc-500 font-mono">{inv.label}</div>
              </div>
              <div>
                {/* Iter-192 — for persisted (non-virtual) rows we now have an explicit
                    `missions_breakdown` from the CRA. Show it instead of the legacy
                    single mission_title (which was always null for cross-mission CRAs). */}
                {inv.missions_breakdown && inv.missions_breakdown.length > 0 ? (
                  <div className="space-y-0.5" data-testid={`fl-inv-missions-${inv.freelance_invoice_id}`}>
                    {inv.missions_breakdown.slice(0, 3).map((m) => (
                      <div key={m.mission_id} className="text-[11px] text-zinc-700 truncate" title={`${m.mission_title} · ${m.client_name || "—"} · ${m.days} J`}>
                        {m.mission_title} <span className="text-zinc-400 font-mono">({m.days}J)</span>
                      </div>
                    ))}
                    {inv.missions_breakdown.length > 3 && (
                      <div className="text-[10px] text-zinc-400">+{inv.missions_breakdown.length - 3} autre(s)</div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-zinc-700 truncate" title={inv.mission_title}>{inv.mission_title || "—"}</div>
                    <div className="text-[11px] text-zinc-500 truncate" title={inv.client_name}>{inv.client_name || "—"}</div>
                  </>
                )}
                {inv.client_invoice_number && (
                  <div className="text-[10px] font-mono text-zinc-400 mt-0.5">via {inv.client_invoice_number}</div>
                )}
                {/* Iter-228x — freelance invoice number (own reference) with copy-to-clipboard.
                    Only shown once submitted (virtual rows have no number yet). */}
                {inv.invoice_number && (
                  <CopyableRef value={inv.invoice_number} testId={`fl-inv-number-${inv.freelance_invoice_id}`} />
                )}
              </div>
              {/* Iter-220 — Cabinet (portage company). */}
              <div className="text-xs text-zinc-700" data-testid={`fl-inv-cabinet-${inv.freelance_invoice_id}`}>
                {inv.portage_company_name ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-brand-cream-100 text-brand-navy text-[11px] font-medium truncate" title={inv.portage_company_name}>
                    {inv.portage_company_name}
                  </span>
                ) : (
                  <span className="text-zinc-400">—</span>
                )}
              </div>
              {/* Iter-192b — submission date column. Falls back to "—" for virtual rows
                  (no submission yet). Display short FR date + time on hover. */}
              <div className="text-xs font-mono" data-testid={`fl-inv-submitted-${inv.freelance_invoice_id}`}>
                {inv.submitted_at ? (
                  (() => {
                    const d = new Date(inv.submitted_at);
                    const short = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
                    const long = d.toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
                    return <span title={long} className="text-zinc-700">{short}</span>;
                  })()
                ) : (
                  <span className="text-zinc-400">—</span>
                )}
              </div>
              <div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono ${meta.color}`} data-testid={`fl-inv-status-${inv.freelance_invoice_id}`}>
                  <Icon size={10} /> {meta.label}
                </span>
                {inv.status === "AWAITING_UNLOCK" && typeof days === "number" && (
                  <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-mono px-1.5 h-5 rounded ${overdue ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-blue-50 text-blue-800 border border-blue-200"}`} data-testid={`fl-inv-countdown-${inv.freelance_invoice_id}`}>
                    <Clock size={9} /> {overdue ? "Ready to unlock" : `${days}j`}
                  </div>
                )}
                {inv.status === "INVOICE_REJECTED" && inv.rejection_reason && (
                  <div className="text-[11px] text-red-700 mt-1 font-mono" title={inv.rejection_reason}>{inv.rejection_reason.slice(0, 40)}…</div>
                )}
                {/* Iter-193 — payment due-date badge once the invoice has been validated. */}
                {inv.status === "INVOICE_PROCESSING" && inv.payment_due_date && (() => {
                  const due = new Date(inv.payment_due_date + "T12:00:00");
                  const d = inv.days_until_due;
                  const dueFmt = due.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
                  let tone = "bg-zinc-50 text-zinc-700 border-zinc-200";
                  let label = `Échéance ${dueFmt}`;
                  if (d == null) {
                    /* keep default */
                  } else if (d < 0) {
                    tone = "bg-red-50 text-red-800 border-red-200";
                    label = `Retard ${-d} j (échue ${dueFmt})`;
                  } else if (d <= 7) {
                    tone = "bg-amber-50 text-amber-800 border-amber-200";
                    label = `J-${d} · ${dueFmt}`;
                  } else {
                    tone = "bg-emerald-50 text-emerald-800 border-emerald-200";
                    label = `J-${d} · ${dueFmt}`;
                  }
                  return (
                    <div className={`mt-1 inline-flex items-center gap-1.5 text-[10px] font-mono px-1.5 h-5 rounded border ${tone}`} title={`Délai accordé : ${inv.payment_delay_days} j`} data-testid={`fl-inv-due-${inv.freelance_invoice_id}`}>
                      <Clock size={9} /> {label}
                      {canEditDelay && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setEditDelayInv(inv); }}
                          className="ml-1 inline-flex items-center hover:text-zinc-900 opacity-70 hover:opacity-100"
                          data-testid={`fl-inv-due-edit-${inv.freelance_invoice_id}`}
                          title="Modifier le délai d'échéance"
                        >
                          <Pencil size={9} />
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="text-xs font-mono">
                {/* Iter-192 — full breakdown HT / TVA / TTC + JH validés (vs facturés). */}
                {inv.amount_ht ? (
                  <div className="space-y-0.5" data-testid={`fl-inv-amounts-${inv.freelance_invoice_id}`}>
                    <div>{Number(inv.amount_ht).toLocaleString("fr-FR")} <span className="text-[9px] text-zinc-500">HT</span></div>
                    {inv.amount_tva != null && (
                      <div className="text-[10px] text-zinc-600">TVA {Number(inv.amount_tva).toLocaleString("fr-FR")}</div>
                    )}
                    {inv.amount_ttc != null && (
                      <div className="text-[10px] font-semibold">{Number(inv.amount_ttc).toLocaleString("fr-FR")} <span className="text-[9px] text-zinc-500">TTC</span></div>
                    )}
                    {inv.approved_jh > 0 && (
                      <div
                        className={`text-[10px] ${inv.amount_ht_mismatch ? (inv.amount_ht_under_billed ? "text-amber-700" : "text-red-700") : "text-zinc-500"}`}
                        title={
                          inv.amount_ht_mismatch
                            ? (inv.amount_ht_under_billed
                                ? `Sous-facturation : attendu ${inv.expected_amount_ht} HT pour ${inv.expected_jh} J (écart ${inv.amount_ht_gap})`
                                : `Attendu ${inv.expected_amount_ht} HT pour ${inv.expected_jh} J`)
                            : "Cohérent avec le CRA"
                        }
                      >
                        {inv.approved_jh} J validés
                        {inv.amount_ht_mismatch && inv.amount_ht_under_billed && (
                          <span
                            data-testid={`fl-inv-under-billed-${inv.freelance_invoice_id}`}
                            className="inline-flex items-center gap-0.5 ml-1 px-1 rounded bg-amber-50 border border-amber-200 text-amber-800"
                          >
                            <AlertTriangle size={8} /> facture &lt; CRA ({inv.amount_ht_gap})
                          </span>
                        )}
                        {inv.amount_ht_mismatch && !inv.amount_ht_under_billed && (
                          <span data-testid={`fl-inv-mismatch-${inv.freelance_invoice_id}`} className="inline-flex items-center gap-0.5 ml-1 px-1 rounded bg-red-50 border border-red-200 text-red-700">
                            <AlertTriangle size={8} /> écart
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ) : inv.approved_jh > 0 ? (
                  <div className="text-[10px] text-zinc-500">{inv.approved_jh} J validés</div>
                ) : "—"}
              </div>
              <div className="flex items-center justify-end gap-1.5">
                {/* PDF link (real rows only) */}
                {inv.drive_url && (
                  <a href={inv.drive_url} target="_blank" rel="noreferrer" className="p-2 rounded-md hover:bg-brand-cream-200" title="Ouvrir le PDF" data-testid={`fl-inv-view-${inv.freelance_invoice_id}`}>
                    <ExternalLink size={14} />
                  </a>
                )}
                {/* AWAITING_UNLOCK — Force unlock (CEO only) */}
                {inv.status === "AWAITING_UNLOCK" && isCEO && inv.client_invoice_id && (
                  <button
                    onClick={() => forceUnlockClientInvoice(inv.client_invoice_id, inv.freelance_invoice_id)}
                    disabled={busyId === inv.freelance_invoice_id}
                    data-testid={`fl-inv-force-unlock-${inv.freelance_invoice_id}`}
                    className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
                    title="Forcer le déblocage du compte à rebours (override CEO)"
                  >
                    <Unlock size={12} /> Force unlock
                  </button>
                )}
                {/* AWAITING_SUBMISSION — Send reminder */}
                {inv.status === "AWAITING_SUBMISSION" && inv.client_invoice_id && (
                  <button
                    onClick={() => remindFreelance(inv.client_invoice_id, inv.user_id, inv.freelance_invoice_id)}
                    disabled={busyId === inv.freelance_invoice_id}
                    data-testid={`fl-inv-remind-${inv.freelance_invoice_id}`}
                    className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
                    title="Envoyer un rappel email au freelance pour qu'il soumette sa facture"
                  >
                    <Bell size={12} /> Send reminder
                  </button>
                )}
                {/* INVOICE_UNDER_REVIEW — Validate / Reject */}
                {inv.status === "INVOICE_UNDER_REVIEW" && (
                  <>
                    <button onClick={() => { setValidateDelay(defaultDelay); setValidateDialog(inv); }} disabled={busyId === inv.freelance_invoice_id} data-testid={`fl-inv-validate-${inv.freelance_invoice_id}`} className="p-2 rounded-md text-emerald-700 hover:bg-emerald-50 disabled:opacity-50" title="Valider">
                      <CheckCircle2 size={15} />
                    </button>
                    <button onClick={() => setRejectDialog(inv)} data-testid={`fl-inv-reject-${inv.freelance_invoice_id}`} className="p-2 rounded-md text-red-600 hover:bg-red-50" title="Rejeter">
                      <X size={15} />
                    </button>
                  </>
                )}
                {/* INVOICE_PROCESSING — Mark paid */}
                {inv.status === "INVOICE_PROCESSING" && (
                  <button onClick={() => markPaidOne(inv.freelance_invoice_id)} disabled={busyId === inv.freelance_invoice_id} data-testid={`fl-inv-paid-${inv.freelance_invoice_id}`} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
                    <Banknote size={12} /> Régler
                  </button>
                )}
                {/* Iter-228x — INVOICE_PAID — manual resend of the portage-company email. */}
                {inv.status === "INVOICE_PAID" && (
                  <button
                    onClick={() => resendPortageEmail(inv.freelance_invoice_id)}
                    disabled={busyId === inv.freelance_invoice_id}
                    data-testid={`fl-inv-resend-portage-${inv.freelance_invoice_id}`}
                    title={inv.portage_email_last_resent_at
                      ? `Dernier renvoi : ${new Date(inv.portage_email_last_resent_at).toLocaleString("fr-FR")}`
                      : "Renvoyer manuellement l'email au cabinet de portage"}
                    className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md border border-blue-200 text-blue-700 text-xs hover:bg-blue-50 disabled:opacity-50"
                  >
                    {busyId === inv.freelance_invoice_id
                      ? <RefreshCw size={12} className="animate-spin" />
                      : <Send size={12} />}
                    Renvoyer portage
                    {inv.portage_email_resent_count > 0 && (
                      <span className="ml-1 px-1 rounded bg-blue-100 text-[9px] font-mono">{inv.portage_email_resent_count}</span>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => !o && setRejectDialog(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Rejeter la facture</DialogTitle>
            <DialogDescription>The freelance will be notified and can resubmit a new invoice for that month.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Motif du refus *</Label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason"
              className="w-full mt-2 p-3 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy/30 min-h-[100px]"
              placeholder="e.g. Wrong invoice number, amount mismatch…"
            />
          </div>
          <DialogFooter>
            <button onClick={() => setRejectDialog(null)} className="px-4 h-10 text-sm rounded-md hover:bg-zinc-100">Cancel</button>
            <button
              onClick={submitReject}
              data-testid="reject-submit"
              className="inline-flex items-center gap-2 px-4 h-10 bg-red-600 text-white text-sm font-semibold rounded-md hover:bg-red-700"
            >
              <AlertTriangle size={14} /> Rejeter
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-193 — validation dialog: ask for payment delay (days from today). */}
      <Dialog open={!!validateDialog} onOpenChange={(o) => !o && setValidateDialog(null)}>
        <DialogContent data-testid="validate-dialog">
          <DialogHeader>
            <DialogTitle>Valider la facture</DialogTitle>
            <DialogDescription>
              Indique le délai de paiement (en jours). La date d'échéance est calculée à partir d'aujourd'hui ; des alertes seront émises au CEO et au gestionnaire si elle n'est pas réglée dans les seuils paramétrés.
            </DialogDescription>
          </DialogHeader>
          {validateDialog && (
            <div className="space-y-3 py-2">
              <div className="text-sm text-zinc-700">
                <strong>{validateDialog.user_name}</strong> · {validateDialog.label} · {validateDialog.amount_ttc} {validateDialog.currency} TTC
              </div>
              {/* Iter-228x-bis — explicit under-billing notice during validation.
                  The freelance is allowed to bill less than the validated JH,
                  but the admin must be informed before approving the invoice. */}
              {validateDialog.amount_ht_under_billed && (
                <div
                  data-testid="validate-under-billed-notice"
                  className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2"
                >
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">Attention — facture inférieure au CRA</div>
                    <div>
                      Cette facture déclare <strong>{validateDialog.amount_ht} {validateDialog.currency}</strong> HT
                      alors que le CRA validé représente <strong>{validateDialog.expected_amount_ht} {validateDialog.currency}</strong> HT
                      ({validateDialog.expected_jh} J × TJM).
                      Écart : <strong>{validateDialog.amount_ht_gap} {validateDialog.currency}</strong>.
                    </div>
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="payment-delay">Délai de paiement (jours)</Label>
                <Input
                  id="payment-delay"
                  data-testid="validate-payment-delay"
                  type="number" min="0" max="180" step="1"
                  value={validateDelay}
                  onChange={(e) => setValidateDelay(parseInt(e.target.value || "0", 10))}
                />
                {validateDelay >= 0 && (
                  <div className="text-[11px] text-zinc-500 font-mono mt-1">
                    Date d'échéance estimée : {new Date(Date.now() + validateDelay * 86400000).toLocaleDateString("fr-FR")}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setValidateDialog(null)} className="px-4 h-10 text-sm rounded-md hover:bg-zinc-100">Annuler</button>
            <button
              onClick={() => validateOne(validateDialog.freelance_invoice_id, validateDelay)}
              data-testid="validate-submit"
              disabled={busyId === validateDialog?.freelance_invoice_id || validateDelay < 0}
              className="inline-flex items-center gap-2 px-4 h-10 bg-emerald-600 text-white text-sm font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-50"
            >
              <Check size={14} /> Valider
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-193c — Edit payment delay dialog */}
      <EditPaymentDelayDialog
        invoice={editDelayInv}
        onClose={() => setEditDelayInv(null)}
        onSaved={(resp) => {
          setInvoices((arr) => arr.map((it) => it.freelance_invoice_id === resp.freelance_invoice_id
            ? {
                ...it,
                payment_delay_days: resp.payment_delay_days,
                payment_due_date: resp.payment_due_date,
                days_until_due: null,  // forces a refresh on next reload
              }
            : it));
          setEditDelayInv(null);
          // Trigger a soft reload so days_until_due/alert_level are recomputed server-side.
          load();
        }}
      />

      {/* Iter-228x — Payment dialog (single-invoice "Régler" button). */}
      <MarkPaidDialog
        state={payDialog}
        setState={setPayDialog}
        bankAccounts={bankAccounts}
        onSubmit={submitMarkPaid}
        busy={busyId === payDialog?.invoice?.freelance_invoice_id}
      />
    </div>
  );
}

function EditPaymentDelayDialog({ invoice, onClose, onSaved }) {
  const [val, setVal] = useState(invoice?.payment_delay_days ?? 30);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(invoice?.payment_delay_days ?? 30); }, [invoice]);
  if (!invoice) return null;
  const submit = async () => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0 || n > 365) {
      toast.error("Délai invalide (0-365 jours)");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/freelance/admin/invoices/${invoice.freelance_invoice_id}/payment-delay`, { payment_delay_days: n });
      toast.success(`Délai mis à jour — nouvelle échéance ${r.data.payment_due_date}`);
      onSaved && onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent data-testid="edit-payment-delay-dialog">
        <DialogHeader>
          <DialogTitle>Modifier le délai d'échéance</DialogTitle>
          <DialogDescription>
            Facture {invoice.invoice_number || invoice.freelance_invoice_id} · {invoice.user_name}.
            La nouvelle échéance est recalculée à partir de la date de validation. Le changement est tracé dans le journal d'audit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs font-mono text-zinc-500">
            Délai actuel : <strong className="text-zinc-800">{invoice.payment_delay_days != null ? `${invoice.payment_delay_days} j` : "—"}</strong>
            {invoice.payment_due_date && (
              <> · Échéance actuelle : <strong className="text-zinc-800">{invoice.payment_due_date}</strong></>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Nouveau délai (jours)</Label>
            <input
              type="number"
              min={0}
              max={365}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className="w-24 px-2 py-1.5 border border-zinc-300 rounded text-sm font-mono"
              autoFocus
              data-testid="edit-payment-delay-input"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50"
            data-testid="edit-payment-delay-cancel"
          >Annuler</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-3 h-9 text-sm bg-zinc-950 text-white rounded hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="edit-payment-delay-save"
          >
            {saving && <RefreshCw size={12} className="animate-spin" />}
            Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Iter-228x — Compact pill that displays a reference value with a click-to-copy
// icon. Used to surface the freelance invoice number on each row.
function CopyableRef({ value, testId }) {
  const onCopy = (e) => {
    e.stopPropagation();
    if (!navigator.clipboard) {
      toast.error("Presse-papiers indisponible");
      return;
    }
    navigator.clipboard.writeText(value)
      .then(() => toast.success(`Référence copiée : ${value}`))
      .catch(() => toast.error("Échec de la copie"));
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      title={`Copier la référence : ${value}`}
      className="inline-flex items-center gap-1 mt-1 px-1.5 h-5 rounded bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 text-[10px] font-mono text-zinc-700 transition-colors group"
    >
      <span className="truncate max-w-[180px]">{value}</span>
      <Copy size={10} className="text-zinc-400 group-hover:text-zinc-700 shrink-0" />
    </button>
  );
}


// Iter-228x — Mark-paid dialog: captures payment details (date, reference,
// method, amount, BAIM bank account, notes). On submit, the parent triggers
// the backend that classifies the invoice + emails the portage company with
// the freelancer in CC and the PDF attached.
function MarkPaidDialog({ state, setState, bankAccounts, onSubmit, busy }) {
  if (!state) return null;
  const inv = state.invoice;
  const upd = (k, v) => setState({ ...state, [k]: v });
  const userLabel = inv.user_name || inv.user_id;
  const monthLabel = `${String(inv.month).padStart(2, "0")}/${inv.year}`;
  return (
    <Dialog open onOpenChange={(o) => !o && setState(null)}>
      <DialogContent className="max-w-xl" data-testid="freelance-mark-paid-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote size={18} /> Régler la facture freelance
          </DialogTitle>
          <DialogDescription className="text-[11px] text-zinc-500 leading-relaxed">
            <strong>{userLabel}</strong> · {monthLabel} ·{" "}
            <span className="font-mono">{Number(inv.amount_ttc ?? 0).toLocaleString("fr-FR")} {inv.currency || "MAD"} TTC</span>
            <br />
            La validation classe la facture et envoie un email de confirmation au cabinet de portage
            avec le freelancer en copie et la facture PDF en pièce jointe.
          </DialogDescription>
        </DialogHeader>
        {/* Iter-228x — Facture freelance reference, prominent + click-to-copy. */}
        {inv.invoice_number && (
          <div className="-mt-1 mb-1 flex items-center gap-2 flex-wrap" data-testid="markpaid-invoice-ref-block">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 m-0">
              Référence de la facture
            </Label>
            <CopyableRef value={inv.invoice_number} testId="markpaid-invoice-number" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Date de paiement</Label>
            <Input
              type="date"
              value={state.paid_date}
              onChange={(e) => upd("paid_date", e.target.value)}
              data-testid="markpaid-date"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Référence du règlement</Label>
            <Input
              value={state.payment_reference}
              onChange={(e) => upd("payment_reference", e.target.value)}
              placeholder="ex. VIR-20260615-001"
              data-testid="markpaid-reference"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Mode</Label>
            <select
              value={state.payment_method}
              onChange={(e) => upd("payment_method", e.target.value)}
              data-testid="markpaid-method"
              className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-md bg-white"
            >
              <option value="VIREMENT">Virement bancaire</option>
              <option value="CHEQUE">Chèque</option>
              <option value="ESPECES">Espèces</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Montant réglé</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.01"
                value={state.amount_paid}
                onChange={(e) => upd("amount_paid", e.target.value)}
                data-testid="markpaid-amount"
              />
              <span className="text-xs text-zinc-500 font-mono">{inv.currency || "MAD"}</span>
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">
              Par défaut : TTC de la facture ({Number(inv.amount_ttc ?? 0).toLocaleString("fr-FR")} {inv.currency || "MAD"}).
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Compte BAIM débité (optionnel)</Label>
            <select
              value={state.bank_account_id}
              onChange={(e) => upd("bank_account_id", e.target.value)}
              data-testid="markpaid-bank-account"
              className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-md bg-white"
            >
              <option value="">— Non spécifié —</option>
              {bankAccounts.map((b) => (
                <option key={b.bank_account_id} value={b.bank_account_id}>
                  [{b.scope === "MAD" ? "MAD" : "INTL"}] {b.label}{b.currency ? ` · ${b.currency}` : ""}{b.is_default ? " (défaut)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Remarques (optionnel)</Label>
            <textarea
              value={state.notes}
              onChange={(e) => upd("notes", e.target.value)}
              rows={2}
              data-testid="markpaid-notes"
              className="w-full text-sm border border-zinc-200 rounded-md p-2"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={() => setState(null)}
            className="px-4 h-10 text-sm rounded-md hover:bg-zinc-100"
            data-testid="markpaid-cancel"
          >
            Annuler
          </button>
          <button
            onClick={onSubmit}
            disabled={busy || !state.paid_date || !state.payment_reference.trim()}
            data-testid="markpaid-submit"
            className="inline-flex items-center gap-2 px-4 h-10 bg-emerald-600 text-white text-sm font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? <RefreshCw size={12} className="animate-spin" /> : <Banknote size={14} />}
            {busy ? "Règlement en cours…" : "Valider le règlement"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
